import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useLogger } from '../providers'

const REFERRING_USER_NAME_KEY = 'referring_user_name'
const REFERRING_USER_ID_KEY = 'referring_user_id'

/**
 * Custom hook to manage the referring user name.
 *
 * This hook retrieves the referring user name from the URL query parameter (`referring_user_name`)
 * or from localStorage. If the query parameter is present, it takes precedence and is persisted
 * to localStorage. Returns the current referring user name.
 * Should be formatted as `referral/${referrer_id}`
 *
 * @returns {{ referringUserName: string | undefined }} An object containing the referring user name.
 */
export function useReferringUser() {
  const logger = useLogger('useReferringUser')
  const [query] = useSearchParams()
  const queryParamReferringUserName = query.get('referring_user_name') || ''
  const queryParamReferringUserId = query.get('referring_user_id') || ''

  const [referringUserName, setReferringUserName] = useState(() => {
    try {
      // Initialize the state with the referring user name from localStorage
      return window.localStorage.getItem(REFERRING_USER_NAME_KEY) || undefined
    } catch (error: any) {
      logger.error('Could not access localStorage:', error?.message)
      return undefined
    }
  })

  const [referringUserId, setReferringUserId] = useState(() => {
    try {
      // Initialize the state with the referring user id from localStorage
      return window.localStorage.getItem(REFERRING_USER_ID_KEY) || undefined
    } catch (error: any) {
      logger.error('Could not access localStorage:', error?.message)
      return undefined
    }
  })

  useEffect(() => {
    if (queryParamReferringUserName && queryParamReferringUserName !== referringUserName) {
      try {
        // If the name from the query is different from the one in state, persist the new name to localStorage
        window.localStorage.setItem(REFERRING_USER_NAME_KEY, queryParamReferringUserName)
        setReferringUserName(queryParamReferringUserName)
      } catch (error: any) {
        logger.error('Could not set item in localStorage:', error?.message)
      }
    }
    if (queryParamReferringUserId && queryParamReferringUserId !== referringUserId) {
      try {
        // If the id from the query is different from the one in state, persist the new id to localStorage
        window.localStorage.setItem(REFERRING_USER_ID_KEY, queryParamReferringUserId)
        setReferringUserId(queryParamReferringUserId)
      } catch (error: any) {
        logger.error('Could not set item in localStorage:', error?.message)
      }
    }
  }, [queryParamReferringUserName, referringUserName, queryParamReferringUserId, referringUserId])

  return {
    referringUserName,
    referringUserId,
  }
}
